/**
 * Estado do robo e vencimento da credencial — formatacao do veredito do servidor.
 *
 * Quem DECIDE e' o backend (`src/consoleai/robo/estado.py`, TASK-11258): o alarme
 * de robo mudo e' o segundo consumidor do mesmo veredito, e duas copias divergiriam
 * (a tela dizendo "Ativo" e o feed "sem contato"). Aqui ficam so' rotulo, cor e
 * significado. Os "2 minutos" e "15 minutos" do texto sao os limiares de la.
 */
import type { ChaveAviso, ChaveEstado } from '../../api/robo'

export type { ChaveAviso, ChaveEstado }

export interface EstadoRobo {
  chave: ChaveEstado
  rotulo: string
  /** Cor Mantine. Acompanha o rotulo, nunca o substitui. */
  cor: string
  /** O que o estado significa de verdade — vai no tooltip. */
  significado: string
}

const ESTADOS: Record<ChaveEstado, Omit<EstadoRobo, 'chave'>> = {
  nunca_conectou: {
    rotulo: 'Nunca conectou', cor: 'gray',
    significado: 'Cadastrado e jamais falou com o Console. Ausência de dado não é "sem contato".',
  },
  ativo: {
    rotulo: 'Ativo', cor: 'green',
    significado: 'Falou com o Console há menos de 2 minutos.',
  },
  atrasado: {
    rotulo: 'Atrasado', cor: 'yellow',
    significado: 'Perdeu de 4 a 30 polls. Ainda pode ser rede.',
  },
  sem_contato: {
    rotulo: 'Sem contato', cor: 'red',
    significado: 'Mais de 15 minutos sem falar. Alguém precisa olhar.',
  },
  suspenso: {
    rotulo: 'Suspenso', cor: 'gray',
    significado: 'Decisão humana, não falha.',
  },
  aposentado: {
    rotulo: 'Aposentado', cor: 'dark',
    significado: 'Retirado da frota. A credencial dele não abre canal.',
  },
}

export function estadoDoRobo(robo: { estado: ChaveEstado }): EstadoRobo {
  return { chave: robo.estado, ...ESTADOS[robo.estado] }
}

export interface AvisoCredencial {
  chave: ChaveAviso
  diasRestantes: number | null
}

/**
 * A chave vem do servidor; os dias sao so' para o texto ("Vence em 5 dias").
 * `token_expires_at` e' epoch em SEGUNDOS, enquanto `agoraMs` e' milissegundos.
 */
export function avisoDeCredencial(
  robo: { aviso_credencial: ChaveAviso; token_expires_at: number | null | undefined },
  agoraMs: number,
): AvisoCredencial {
  if (robo.aviso_credencial === 'sem_credencial' || !robo.token_expires_at) {
    return { chave: robo.aviso_credencial, diasRestantes: null }
  }
  const dias = Math.floor((robo.token_expires_at * 1000 - agoraMs) / 86400_000)
  return { chave: robo.aviso_credencial, diasRestantes: dias }
}

/** A partir de quando um recebimento sem resposta vira selo na Frota (spec 2026-09-27, 4.6). */
export const ENTREGA_PENDENTE_DIAS = 7

export type SeloDeEntrega = { rotulo: string; cor: string } | null

/**
 * O selo de entrega da Frota. Recusa do dono aparece sempre; pendência só
 * depois de `ENTREGA_PENDENTE_DIAS`, porque todo robô recém-atribuído está
 * pendente e selo em toda linha não diz nada.
 */
export function seloDeEntrega(
  r: { receipt_state?: string | null; assigned_at?: string | null }, agora: number,
): SeloDeEntrega {
  if (r.receipt_state === 'nao_recebido') return { rotulo: 'Entrega não confirmada', cor: 'red' }
  if (r.receipt_state !== 'pendente' || !r.assigned_at) return null
  const desde = Date.parse(r.assigned_at)
  if (Number.isNaN(desde)) return null
  return agora - desde > ENTREGA_PENDENTE_DIAS * 86400_000
    ? { rotulo: 'Entrega pendente', cor: 'yellow' }
    : null
}
